import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    useToast,
    UseToastOptions,
    Stack,
    Input,
    InputGroup,
    InputLeftElement,
    Flex,
    Switch,
    Text,
    Box,
    Tooltip
} from "@chakra-ui/react";
import ConfirmBox from "components/custom-components/Alerts/ConfirmBox";
import { useNavigate, useParams } from 'react-router-dom';
import useServerApi from '../../hooks/useServerApi';
import Wrapper from './Wrapper';
import Loader from './Loader';
import CallTransferEdit from "components/custom-components/Edit/CallTransferEdit";
import CallTransferListDisplay from './CallTransferListDisplay';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useUserData } from 'context/UserDataProvider';
import type { UserData } from 'hooks/useGetUserData';
import { PATHS } from 'router/routes';

// Debounce hook for stable debounced callbacks
function useDebouncedCallback<T extends (...args: any[]) => void>(
    callback: T,
    delay: number
): T {
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const debouncedFn = React.useCallback((...args: any[]) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            callback(...args);
        }, delay);
    }, [callback, delay]);

    // @ts-ignore
    return debouncedFn;
}

type CallTransfer = {
    id: string;
    beforTransferingMsg?: string;
    instructionBeforTransfer?: string;
    callerIdStatus?: string;
    warmTransferSettings?: {
        typeWarmTransfer?: string;
        onNoPickup?: string;
        templateMessage?: string;
    };
    [key: string]: any;
};

type CallTransfersResponse = {
    transfers: CallTransfer[];
    count: number;
    pagination: {
        page: number;
        perPage: number;
    };
    active?: boolean;
};

type EditWindowProps = {
    editRow: CallTransfer | null;
    setEditRow: React.Dispatch<React.SetStateAction<CallTransfer | null>>;
    agentId: string;
    post: (...args: any[]) => Promise<any>;
    setCallTransfers: React.Dispatch<React.SetStateAction<CallTransfersResponse>>;
    toast: ReturnType<typeof useToast>;
    warmTransferEnabled: boolean;
    isWarmTransferLocked: boolean;
    onUpgrade: () => void;
};

type RowActions = {
    edit: (row: CallTransfer) => void;
    delete: (row: CallTransfer) => void;
};

const callerIdStatusDict: Record<string, string> = {
    1: "Show caller's phone numbers",
    2: "Show AI agent's phone number"
};

const CallTransfers: React.FC = () => {
    const params = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const agentId = params.id as string;
    const { userData: userDataFromContext } = useUserData();
    const userData = userDataFromContext as UserData | null;
    const [callTransfers, setCallTransfers] = useState<CallTransfersResponse>({ transfers: [], count: 0, pagination: { page: 1, perPage: 10 }, active: false });
    const [loading, setLoading] = useState(true);
    const [{ post, get }] = useServerApi();
    const [editRow, setEditRow] = useState<CallTransfer | null>(null);
    // Dialog state for delete confirmation
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<CallTransfer | null>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    // Pagination and filter state
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [switchLoading, setSwitchLoading] = useState(false);
    const warmTransferEnabled = userData?.has_premium_subscription ?? false;
    const isWarmTransferLocked = !warmTransferEnabled;
    
    // Always reads from state, no parameters
    const fetchCallTransfers = useCallback(async () => {
        setLoading(true);
        const reqPage = page ? page : 1;
        const reqPerPage = perPage ? perPage : 10;
        const reqQuery = query ? query : '';
        const payload = { pagination: { page: reqPage, perPage: reqPerPage }, filter: { query: '' } };
        if (reqQuery) payload.filter = { query: reqQuery };
        const [success, res] = await post(`/rest_endpoints/${agentId}/call_transfers/list`, payload);
        if (success) setCallTransfers(res);
        setLoading(false);
    }, [page, perPage, query, post, agentId]);

    // Only updates state, fetch is triggered by useEffect
    const debouncedFetchCallTransfers = useDebouncedCallback(
        (q: string) => {
            setPage(1);
            setQuery(q);
        },
        400
    );

    const refreshCallTransfers = async () => {
        await fetchCallTransfers();
    };

    const deleteRow = async () => {
        setIsDialogOpen(false);
        const [success] = await get(`/rest_endpoints/${agentId}/call_transfers/delete/${rowToDelete?.id}`);
        if (!success) {
            showToast(toast, "Error", "Failed to delete call transfer.", "error");
            return;
        }
        showToast(toast, "Success", "Call transfer deleted successfully.", "success");
        setRowToDelete(null);
        await refreshCallTransfers();
    };

    const onDelete = (row: CallTransfer) => {
        setRowToDelete(row);
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setRowToDelete(null);
    };

    const changeCallActiveStatus = async (change: boolean) => {
        setSwitchLoading(true);
        const [success] = await get(`/rest_endpoints/${agentId}/call_transfers/change_status/${change ? 'enabled' : 'disabled'}`);
        if (!success) {
            showToast(toast, "Error", "Failed to change call transfer status.", "error");
            setSwitchLoading(false);
            return;
        }
        setCallTransfers((prev: CallTransfersResponse) => ({ ...prev, active: change }));
        showToast(toast, "Success", "Call transfer status updated successfully.", "success");
        setSwitchLoading(false);
    }

    useEffect(() => {
        if (!agentId) return;
        fetchCallTransfers();
    }, [agentId, page, perPage, query, fetchCallTransfers]);
    if (loading || !callTransfers) return <Loader />;

    return (
        <Wrapper title="Transfer Calls" description="Configure when and how to transfer calls">
            <Stack direction='row' justifyContent='space-between' flexWrap='wrap'>
                <InputGroup width='288px'>
                    <InputLeftElement
                        pointerEvents="none"
                        width="2.5em"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <FontAwesomeIcon icon={faSearch} color='gray' />
                    </InputLeftElement>
                    <Input
                        fontSize='calc(12px + 4  * (100vw / 1920))'
                        placeholder="Search by phone, rule, or message"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => debouncedFetchCallTransfers(e.target.value)}
                        pl="2.5em"
                    />
                </InputGroup>
                <Flex
                    fontSize={{ base: '24px', sm: '28px' }}
                    lineHeight='28px'
                    fontWeight={{ base: '400', sm: '700' }}
                    textAlign='left'
                    gap='10px'
                    alignItems='center'
                    mb='10px'
                >
                    <Tooltip
                        label={"Toggle call transfer functionality"}
                        placement="top"
                        hasArrow
                    >
                        <Switch
                            isDisabled={loading || switchLoading}
                            isChecked={callTransfers?.active ? true : false}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => changeCallActiveStatus(e.target.checked)}
                            size='lg'
                        />
                    </Tooltip>
                    <Text>Transfer Calls</Text>
                </Flex>

            </Stack>
            <CallTransferListDisplay
                callTransfers={callTransfers}
                setEditRow={setEditRow}
                onDelete={onDelete}
                page={page}
                perPage={perPage}
                onPageChange={(newPage: number) => setPage(newPage)}
                onPerPageChange={(newPerPage: number) => {
                    setPerPage(newPerPage);
                    setPage(1);
                }}
                warmTransferEnabled={warmTransferEnabled}
            />
            <EditWindow
                editRow={editRow}
                setEditRow={setEditRow}
                agentId={agentId}
                post={post}
                setCallTransfers={setCallTransfers}
                toast={toast}
                refreshCallTransfers={refreshCallTransfers}
                warmTransferEnabled={warmTransferEnabled}
                isWarmTransferLocked={isWarmTransferLocked}
                onUpgrade={() => navigate(PATHS.UPGRADE_PLAN)}
            />

            <ConfirmBox
                isOpen={isDialogOpen}
                onClose={handleDialogClose}
                onConfirm={deleteRow}
                title="Delete Call Transfer"
                description="Are you sure you want to delete this call transfer?"
                cancelRef={cancelRef}
                confirmButtonText="Delete"
                confirmButtonColorScheme="red"
                cancelButtonText="Cancel"
            />
        </Wrapper >
    );
};

function showToast(
    toast: ReturnType<typeof useToast>,
    title: string,
    message: string,
    status: UseToastOptions["status"]
) {
    toast({
        title,
        description: message,
        status,
        duration: 4000,
        isClosable: true,
        position: "top"
    });
}


const EditWindow = (props: EditWindowProps & { refreshCallTransfers: () => Promise<void> }) => {
    const { editRow, setEditRow, agentId, post, toast, refreshCallTransfers, isWarmTransferLocked, onUpgrade } = props;
    if (!editRow) return null;
    // Always ensure phoneNumber is present for the form
    const editValues = { phoneNumber: '', ...editRow };
    const handleEditSave = async (values: any) => {
        if (!values.callerIdStatus || values.callerIdStatus === "0") {
            showToast(toast, "Error", "Caller ID status is required.", "error");
            return false;
        }
        const { preferredSchedule, warmTransferSettings, ...rest } = values;
        if (!editRow.id) {
            const payload = { ...rest, preferredSchedule, warmTransferSettings };
            if (payload.id === "") delete payload.id;
            const [success] = await post(`/rest_endpoints/${agentId}/call_transfers/`, payload);
            if (!success) {
                showToast(toast, "Error", "Failed to create call transfer.", "error");
                setEditRow(values); // Preserve user input on failure
                return false;
            }
            showToast(toast, "Success", "Call transfer created successfully.", "success");
            setEditRow(null);
            await refreshCallTransfers();
            return true;
        }
        const payload = { ...editValues, ...rest, preferredSchedule, warmTransferSettings };
        const [success] = await post(`/rest_endpoints/${agentId}/call_transfers/update`, payload);
        if (!success) {
            showToast(toast, "Error", "Failed to update call transfer.", "error");
            setEditRow(payload);
            return false;
        }
        showToast(toast, "Success", "Call transfer updated successfully.", "success");
        setEditRow(null);
        await refreshCallTransfers();
    };

    const fieldLabelTypes = {
        phoneNumber: { label: "Phone Number", type: "phonenumber" },
        instructionBeforTransfer: { label: "When do you want your agent to transfer calls to this number?", type: "string" },
        beforTransferingMsg: { label: "What do you want your agent to say before it transfers the call?", type: "string" },
        callerIdStatus: {
            label: "Caller Id",
            type: "select",
            values: { 0: "Choose", ...callerIdStatusDict },
        },
        warmTransferSettings: {
            label: "Warm Transfer",
            type: "group",
            fields: {}
        }
    };
    
    return (
        <CallTransferEdit
            values={editValues}
            fieldLabelTypes={fieldLabelTypes}
            showTimeBoundingBox={true}
            onSave={handleEditSave}
            onClose={() => setEditRow(null)}
            displayType="dialog"
            open={true}
            title="Edit Call Transfer"
            isWarmTransferLocked={isWarmTransferLocked}
            onUpgrade={onUpgrade}
        />
    );
};

export default CallTransfers;
